// FormSchema-driven popup form. Renders text /
// textarea / currency / date / dropdown / checkbox / multi-select fields
// per the catalog item's FormSchema. A schema's required flags are not
// shown: nothing enforces them, as Certify checks only the Severity
// Assessment and the report attestation (owner ruling, 2026-10-13).

import { Input, Textarea, FormField } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import type { FormSchema, JustificationData } from "@/lib/formSchema";

interface PopupFormFromSchemaProps {
  schema: FormSchema;
  value: JustificationData;
  onChange: (next: JustificationData) => void;
  disabled?: boolean;
}

export function PopupFormFromSchema({
  schema,
  value,
  onChange,
  disabled,
}: PopupFormFromSchemaProps) {
  function setField(name: string, v: JustificationData[string]) {
    onChange({ ...value, [name]: v });
  }

  return (
    <div className="flex flex-col gap-space-3">
      {schema.fields.map((f) => {
        const fieldValue = value[f.name];
        switch (f.type) {
          case "text":
            return (
              <FormField key={f.name} label={f.label} helperText={f.helpText}>
                <Input
                  value={typeof fieldValue === "string" ? fieldValue : ""}
                  disabled={disabled}
                  onChange={(e) => setField(f.name, e.target.value)}
                />
              </FormField>
            );
          case "textarea":
            return (
              <FormField key={f.name} label={f.label} helperText={f.helpText}>
                <Textarea
                  rows={4}
                  value={typeof fieldValue === "string" ? fieldValue : ""}
                  disabled={disabled}
                  onChange={(e) => setField(f.name, e.target.value)}
                />
              </FormField>
            );
          case "currency":
            return (
              <FormField key={f.name} label={f.label} helperText={f.helpText}>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={
                    typeof fieldValue === "number"
                      ? String(fieldValue)
                      : typeof fieldValue === "string"
                        ? fieldValue
                        : ""
                  }
                  disabled={disabled}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setField(f.name, raw === "" ? null : Number(raw));
                  }}
                />
              </FormField>
            );
          case "date":
            return (
              <FormField key={f.name} label={f.label} helperText={f.helpText}>
                <Input
                  type="date"
                  value={typeof fieldValue === "string" ? fieldValue : ""}
                  disabled={disabled}
                  onChange={(e) => setField(f.name, e.target.value || null)}
                />
              </FormField>
            );
          case "dropdown":
            return (
              <FormField key={f.name} label={f.label} helperText={f.helpText}>
                <Select
                  value={typeof fieldValue === "string" ? fieldValue : ""}
                  disabled={disabled}
                  onChange={(e) => setField(f.name, e.target.value || null)}
                >
                  <option value="">Select…</option>
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            );
          case "checkbox":
            return (
              <label
                key={f.name}
                className="flex items-start gap-space-2 text-small text-onyx"
              >
                <input
                  type="checkbox"
                  checked={!!fieldValue}
                  disabled={disabled}
                  onChange={(e) => setField(f.name, e.target.checked)}
                  className="mt-[2px]"
                />
                <span>
                  <span className="block">{f.label}</span>
                  {f.helpText && <HelpText text={f.helpText} />}
                </span>
              </label>
            );
          case "multi-select": {
            const selected = Array.isArray(fieldValue) ? fieldValue : [];
            return (
              <FormField key={f.name} label={f.label} helperText={f.helpText}>
                <div className="flex flex-wrap gap-space-2">
                  {(f.options ?? []).map((o) => {
                    const isOn = selected.includes(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          setField(
                            f.name,
                            isOn
                              ? selected.filter((s) => s !== o.value)
                              : [...selected, o.value],
                          )
                        }
                        className={`px-space-2 py-[3px] text-caption rounded-pill border ${
                          isOn
                            ? "bg-gold/20 border-gold text-onyx"
                            : "bg-white-pure border-border-light text-gray-mid"
                        }`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </FormField>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

function HelpText({ text }: { text: string }) {
  return <p className="text-caption text-gray-mid mt-space-1 m-0">{text}</p>;
}
